import type { AxisScale, AxisScales } from '../types';
import { toCategoryFormat } from './categoryAxes';

type Format = (value: number) => string;

/** How one axis names a value on it, and a bin spanning part of it. */
export type AxisLabel = {
  value: Format;
  bin: (bounds: readonly [number, number]) => string;
};

export type AxisLabels = Record<'x' | 'y' | 'z', AxisLabel>;

const NUMBER_SCALE: AxisScale = { kind: 'number' };

export const NUMBER_AXIS_SCALES: AxisScales = {
  x: NUMBER_SCALE,
  y: NUMBER_SCALE,
  z: NUMBER_SCALE,
};

const toNumberLabel = (format: Format): AxisLabel => ({
  value: format,
  bin: ([low, high]) => `${format(low)} – ${format(high)}`,
});

const toCategoryLabel = (
  values: readonly string[],
  firstPosition: number
): AxisLabel => {
  const formatCategory = toCategoryFormat(values, firstPosition);
  return {
    value: formatCategory,
    bin: ([low, high]) => formatCategory((low + high) / 2),
  };
};

const toAxisLabel = (scale: AxisScale, format: Format): AxisLabel =>
  scale.kind === 'category'
    ? toCategoryLabel(scale.values, scale.firstPosition)
    : toNumberLabel(format);

/** Each axis's labels, by its scale: categories by name, numbers in the shared format. */
export const toAxisLabels = (
  format: Format,
  { x, y, z }: AxisScales
): AxisLabels => ({
  x: toAxisLabel(x, format),
  y: toAxisLabel(y, format),
  z: toAxisLabel(z, format),
});
